import { DiscountProvider } from "@redotech/cockroachdb-codegen/congodb/discount-provider";
import { useLazyContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
} from "@redotech/redo-discounts-common/discount";
import {
  DiscountCategory,
  DiscountSettingsType,
  DynamicRangeExpirationUnits,
  RedoDiscount,
  RedoDraftDiscount,
} from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { formatDiscountDescription } from "@redotech/redo-discounts-common/discount/format-discount-description";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import EditPencilIcon from "@redotech/redo-icons/edit-02.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import SlashCircleIcon from "@redotech/redo-icons/slash-circle-01.svg";
import DiscountIcon from "@redotech/redo-icons/tag-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../alert";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import { RedoListItem } from "../arbiter-components/list/redo-list";
import { RedoModal } from "../arbiter-components/modal/redo-modal";
import {
  RedoDropdownInputSize,
  RedoDropdownInputState,
  RedoSingleSelectDropdownInput,
} from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "../divider";
import { EmailBuilderContext } from "../email-builder/builder/email-builder-context";
import { Flex } from "../flex";
import { Text } from "../text";
import { BigcommerceBasicSettings } from "./discount-settings/bigcommerce-basic-settings";
import { BigcommerceFreeShippingSettings } from "./discount-settings/bigcommerce-free-shipping-settings";
import {
  discountCodeNameTitle,
  discountGroupTypeOptions,
  getDiscountCodeStrategyDescription,
} from "./discount-settings/constants";
import { DynamicRangeSettings } from "./discount-settings/dynamic-range-settings";
import { ShopifyBasicSettings } from "./discount-settings/shopify-basic-settings";
import { ShopifyBxgySettings } from "./discount-settings/shopify-bxgy-settings";
import { ShopifyFreeShippingSettings } from "./discount-settings/shopify-free-shipping-settings";
import { DiscountSettingsSectionProps } from "./discount-settings/types";
import {
  AvailableProvidersContext,
  DiscountMethodsContext,
  DiscountsContext,
  ShopifyBasicDiscountCustomerEligibility,
} from "./discount-shared";
import { useDiscountForm } from "./hooks/use-discount-form";
import { resolveDiscountProvider } from "./resolve-discount-provider";
import { validateDiscountConfiguration } from "./utils/discount-validation";

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export * from "./discount-shared";

const NO_DISCOUNT_ID = "__no_discount__";

function formatDiscountSupportingText(discount: RedoDiscount): string {
  const code = discount.codeGenerationStrategy.code;
  const valueText = formatDiscountDescription({
    title: discount.name,
    discountSettings: discount.discountSettings,
  });
  return code ? `${code} · ${valueText}` : valueText;
}

function formatDiscountSelectedText(discount: RedoDiscount): string {
  const code = discount.codeGenerationStrategy.code;
  const valueText = formatDiscountDescription({
    title: discount.name,
    discountSettings: discount.discountSettings,
  });
  return code ? `${valueText} · ${code}` : valueText;
}

export const DiscountSelector = memo(function DiscountSelector({
  discountId,
  setDiscountId,
  required,
  hideCreateButton,
  excludeDiscountIds,
  showCreateAsDropdownOption,
  category,
  showAllCategories,
  allowedDiscountTypes,
  fitToAnchor,
  showLeadingIcon,
  showDiscountDetails,
  showNoDiscountOption,
  label,
  size = "xs",
  fullWidth,
  state,
}: {
  discountId: string | undefined;
  setDiscountId: (id: string | undefined) => void;
  required?: boolean;
  hideCreateButton?: boolean;
  excludeDiscountIds?: string[];
  showCreateAsDropdownOption?: boolean;
  category?: DiscountCategory;
  /** Show every discount regardless of category (incl. uncategorized). */
  showAllCategories?: boolean;
  /** Settings types available in the inner Create / Edit Discount modal. */
  allowedDiscountTypes?: DiscountSettingsType[];
  fitToAnchor?: boolean;
  showLeadingIcon?: boolean;
  showDiscountDetails?: boolean;
  showNoDiscountOption?: boolean;
  label?: string;
  size?: RedoDropdownInputSize;
  fullWidth?: boolean;
  state?: RedoDropdownInputState;
}) {
  const emailBuilderContext = useContext(EmailBuilderContext);

  const isCampaignTemplate =
    emailBuilderContext?.template?.schemaType === SchemaType.MARKETING_CAMPAIGN;

  const { deleteDiscount } = useContext(DiscountMethodsContext);
  // DiscountModal renders nothing when no provider resolves (discounts are
  // Shopify/BigCommerce-only), so offering Create would be a dead click.
  const availableProviders = useContext(AvailableProvidersContext);
  const canCreateDiscount = resolveDiscountProvider(availableProviders) != null;

  const [discounts] = useLazyContext(DiscountsContext);

  const [discountModalOpen, setDiscountModalOpen] = useState(false);

  const [discountBeingUpdated, setDiscountBeingUpdated] = useState<
    RedoDiscount | undefined
  >();

  const [noDiscountSelected, setNoDiscountSelected] = useState(
    showNoDiscountOption && !discountId,
  );

  const [searchString, setSearchString] = useState("");

  const options: RedoListItem<string>[] = useMemo(() => {
    if (!discounts.value) {
      return [];
    }

    let sortedDiscounts = [...discounts.value];

    // Filter by category
    if (showAllCategories) {
      // No category filtering — surface every discount regardless of origin.
    } else if (category) {
      // If category is specified (e.g., RECOVER), only show discounts with that category
      sortedDiscounts = sortedDiscounts.filter(
        (discount) => discount.category === category,
      );
    } else {
      // If no category specified, only show discounts without a category (exclude RECOVER)
      sortedDiscounts = sortedDiscounts.filter(
        (discount) => !discount.category,
      );
    }

    // Hide DYNAMIC_RANGE unless the caller opts in via allowedDiscountTypes.
    if (!allowedDiscountTypes?.includes(DiscountSettingsType.DYNAMIC_RANGE)) {
      sortedDiscounts = sortedDiscounts.filter(
        (discount) =>
          discount.discountSettings?.settingsType !==
          DiscountSettingsType.DYNAMIC_RANGE,
      );
    }

    if (excludeDiscountIds && excludeDiscountIds.length > 0) {
      sortedDiscounts = sortedDiscounts.filter(
        (discount) => !excludeDiscountIds.includes(discount._id),
      );
    }

    if (searchString) {
      const lowerSearch = searchString.toLowerCase();
      sortedDiscounts = sortedDiscounts.filter((discount) =>
        discount.name.toLowerCase().includes(lowerSearch),
      );
    }

    if (isCampaignTemplate) {
      sortedDiscounts = sortedDiscounts.sort((a, b) => {
        const aIsDynamic =
          a.codeGenerationStrategy.strategy === DiscountGroupType.DYNAMIC;
        const bIsDynamic =
          b.codeGenerationStrategy.strategy === DiscountGroupType.DYNAMIC;
        if (aIsDynamic === bIsDynamic) {
          return 0;
        }
        return aIsDynamic ? 1 : -1;
      });
    }

    const discountOptions: RedoListItem<string>[] = sortedDiscounts.map(
      (discount) => ({
        id: discount._id,
        type: "text" as const,
        text: discount.name,
        value: discount._id,
        ...(showDiscountDetails && {
          supportingText: formatDiscountSupportingText(discount),
        }),
        menu: [
          {
            onClick: async () => {
              setDiscountBeingUpdated(discount);
              setDiscountModalOpen(true);
            },
            text: "Edit",
            Icon: EditPencilIcon,
          },
          {
            onClick: async () => {
              await deleteDiscount(discount._id);
              setDiscountId(undefined);
            },
            text: "Delete",
            Icon: TrashIcon,
            theme: "destructive" as const,
          },
        ],
      }),
    );

    const noDiscountOption: RedoListItem<string>[] = showNoDiscountOption
      ? [
          {
            id: NO_DISCOUNT_ID,
            type: "text" as const,
            text: "No discount",
            value: NO_DISCOUNT_ID,
          },
        ]
      : [];

    if (showCreateAsDropdownOption && canCreateDiscount) {
      return [
        ...noDiscountOption,
        ...discountOptions,
        ...(discountOptions.length > 0
          ? [
              {
                id: "divider-section",
                type: "custom" as const,
                value: "section",
                useWrapper: false,
                render: <Divider />,
              },
            ]
          : []),
        {
          id: "create-discount",
          type: "text" as const,
          text: "Create discount",
          value: "create-discount",
          leadingItem: { type: "icon" as const, Icon: PlusIcon },
        },
      ];
    }

    return [...noDiscountOption, ...discountOptions];
  }, [
    discounts.value,
    setDiscountId,
    deleteDiscount,
    isCampaignTemplate,
    searchString,
    excludeDiscountIds,
    showCreateAsDropdownOption,
    canCreateDiscount,
    category,
    showAllCategories,
    allowedDiscountTypes,
    showDiscountDetails,
    showNoDiscountOption,
  ]);

  const selectedItem = useMemo((): RedoListItem<string> | undefined => {
    const resolvedId = noDiscountSelected ? NO_DISCOUNT_ID : discountId;
    const option = options.find((o) => o.id === resolvedId);
    if (!option) {
      return undefined;
    }

    if (!showDiscountDetails && !showLeadingIcon) {
      return option;
    }

    if (option.id === NO_DISCOUNT_ID) {
      return {
        ...option,
        ...(showLeadingIcon && {
          leadingItem: { type: "icon" as const, Icon: SlashCircleIcon },
        }),
      };
    }

    const discount = discounts.value?.find((d) => d._id === option.id);
    if (!discount) {
      return option;
    }

    return {
      ...option,
      ...(showDiscountDetails && {
        text: formatDiscountSelectedText(discount),
        supportingText: undefined,
      }),
      ...(showLeadingIcon && {
        leadingItem: { type: "icon" as const, Icon: DiscountIcon },
      }),
    };
  }, [
    discountId,
    noDiscountSelected,
    options,
    discounts.value,
    showDiscountDetails,
    showLeadingIcon,
  ]);

  const dynamicDiscountIds = useMemo(() => {
    if (!isCampaignTemplate || !discounts.value) {
      return [];
    }
    return discounts.value
      .filter(
        (discount) =>
          discount.codeGenerationStrategy.strategy ===
          DiscountGroupType.DYNAMIC,
      )
      .map((discount) => discount._id);
  }, [isCampaignTemplate, discounts.value]);

  const disabledTooltips = useMemo(() => {
    const tooltips: Record<string, string> = {};
    for (const id of dynamicDiscountIds) {
      tooltips[id] = "Dynamic discounts cannot be used within campaigns";
    }
    return tooltips;
  }, [dynamicDiscountIds]);

  return (
    <Flex dir="column" gap="sm">
      <RedoSingleSelectDropdownInput
        disabled={
          discounts.value?.length === 0 &&
          !(showCreateAsDropdownOption && canCreateDiscount)
        }
        disabledItems={isCampaignTemplate ? dynamicDiscountIds : undefined}
        disabledTooltips={isCampaignTemplate ? disabledTooltips : undefined}
        fitToAnchor={fitToAnchor}
        fullWidth={fullWidth}
        itemsLoading={discounts.pending}
        label={label}
        options={options}
        optionSelected={({ item }) => {
          if (item.value === "create-discount") {
            setDiscountModalOpen(true);
          } else if (item.value === NO_DISCOUNT_ID) {
            setNoDiscountSelected(true);
            setDiscountId(undefined);
          } else {
            setNoDiscountSelected(false);
            setDiscountId(item.value);
          }
        }}
        placeholder="Select a discount"
        searchPlaceholder="Search discounts"
        searchString={searchString}
        selectedItem={selectedItem}
        setSearchString={setSearchString}
        size={size}
        state={state}
      />
      {required && !discountId && (
        <Text fontSize="xs" textColor="error">
          Discount is required
        </Text>
      )}
      {!hideCreateButton && canCreateDiscount && (
        <RedoButton
          hierarchy="secondary"
          onClick={() => {
            setDiscountModalOpen(true);
          }}
          size="sm"
          text={`${category === DiscountCategory.RECOVER ? "Create Recover discount" : "Create discount"}`}
        />
      )}
      <DiscountModal
        allowedDiscountTypes={allowedDiscountTypes}
        category={category}
        existingDiscount={discountBeingUpdated}
        isCampaignTemplate={isCampaignTemplate}
        isOpen={discountModalOpen}
        key={`${discountModalOpen}`}
        onModalCloseRequested={() => {
          setDiscountBeingUpdated(undefined);
          setDiscountModalOpen(false);
        }}
        value={discountId}
        valueChange={(value) => {
          setDiscountId(value);
          setNoDiscountSelected(false);
        }}
      />
    </Flex>
  );
});

interface DiscountModalProps {
  value?: string | undefined;
  valueChange?: (value: string) => void;
  isCampaignTemplate?: boolean;
  isOpen: boolean;
  onModalCloseRequested: () => void;
  existingDiscount?: RedoDiscount | undefined;
  category?: DiscountCategory;
  allowedDiscountTypes?: DiscountSettingsType[];
  onConfigurationReady?: (config: RedoDraftDiscount) => void;
  initialDynamicRangeConfig?: {
    discountName?: string;
    discountCodePrefix: string;
    minPercentage: number;
    maxPercentage: number;
    expirationValue: number;
    expirationUnits: DynamicRangeExpirationUnits;
    combinesWith?: {
      orderDiscounts: boolean;
      productDiscounts: boolean;
      shippingDiscount: boolean;
    };
    minimumRequirement?: { type: DiscountMinRequirementType; minimum: number };
    customerEligibility?: ShopifyBasicDiscountCustomerEligibility;
    totalUsageLimit?: number;
    appliesOncePerCustomer?: boolean;
  };
}

export const DiscountModal = memo(function DiscountModal(
  props: DiscountModalProps,
) {
  const availableProviders = useContext(AvailableProvidersContext);
  const discountProvider = resolveDiscountProvider(availableProviders);
  // Discounts are Shopify/BigCommerce-only. On other providers render nothing
  // rather than throwing during render (which crashed the tracking-page builder).
  if (!discountProvider) {
    return null;
  }
  return <DiscountModalInner {...props} discountProvider={discountProvider} />;
});

const DiscountModalInner = memo(function DiscountModalInner({
  value: _value,
  valueChange,
  isCampaignTemplate,
  isOpen,
  onModalCloseRequested,
  existingDiscount,
  category,
  allowedDiscountTypes,
  onConfigurationReady,
  initialDynamicRangeConfig,
  discountProvider,
}: DiscountModalProps & { discountProvider: DiscountProvider }) {
  const { updateDiscount, createDiscount } = useContext(DiscountMethodsContext);
  const [showHiddenRecoverFields, setShowHiddenRecoverFields] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const formState = useDiscountForm(existingDiscount, category);

  // Pre-populate from initialDynamicRangeConfig when modal opens
  useEffect(() => {
    if (!isOpen || !initialDynamicRangeConfig || initialized) {
      return;
    }
    formState.setDiscountSettingsType(DiscountSettingsType.DYNAMIC_RANGE);
    formState.setCodeGenerationStrategy(DiscountGroupType.DYNAMIC);
    formState.setCodeStrategyName(initialDynamicRangeConfig.discountCodePrefix);
    formState.setDiscountName(
      initialDynamicRangeConfig.discountName || "Marketing Agent Discount",
    );
    formState.setDynamicRangeMinPercentage(
      initialDynamicRangeConfig.minPercentage,
    );
    formState.setDynamicRangeMaxPercentage(
      initialDynamicRangeConfig.maxPercentage,
    );
    formState.setDynamicRangeExpirationValue(
      initialDynamicRangeConfig.expirationValue,
    );
    formState.setDynamicRangeExpirationUnits(
      initialDynamicRangeConfig.expirationUnits,
    );

    // Pre-populate shared fields
    if (initialDynamicRangeConfig.combinesWith) {
      formState.setCombineDiscountWith(initialDynamicRangeConfig.combinesWith);
    }
    if (initialDynamicRangeConfig.minimumRequirement) {
      const req = initialDynamicRangeConfig.minimumRequirement;
      if (req.type === DiscountMinRequirementType.MINIMUM_QUANTITY) {
        formState.handleSetMinimumQuantity(req.minimum);
      } else if (req.type === DiscountMinRequirementType.MINIMUM_SUBTOTAL) {
        formState.handleSetMinimumSubtotal(req.minimum);
      }
    }
    if (initialDynamicRangeConfig.customerEligibility) {
      formState.setCustomerEligibility(
        initialDynamicRangeConfig.customerEligibility,
      );
    }
    if (initialDynamicRangeConfig.totalUsageLimit) {
      formState.setTotalUsageLimit(initialDynamicRangeConfig.totalUsageLimit);
    }
    if (initialDynamicRangeConfig.appliesOncePerCustomer !== undefined) {
      formState.setAppliesOncePerCustomer(
        initialDynamicRangeConfig.appliesOncePerCustomer,
      );
    }

    setInitialized(true);
  }, [isOpen, initialDynamicRangeConfig, initialized, formState]);

  const discountValidationResult: RedoDraftDiscount | string = useMemo(() => {
    return validateDiscountConfiguration({
      ...formState,
      provider: discountProvider,
      limitToSegments: formState.userSelectedShopifySegments.map(
        (segment) => segment.id,
      ),
      existingDiscount,
      existingDiscounts: formState.discounts.value,
      category,
    });
  }, [formState, existingDiscount, category, discountProvider]);

  const initialConfigRef = useRef<string | null>(null);
  if (
    initialConfigRef.current === null &&
    existingDiscount &&
    typeof discountValidationResult !== "string"
  ) {
    initialConfigRef.current = JSON.stringify(discountValidationResult);
  }

  const isDirty = existingDiscount
    ? typeof discountValidationResult !== "string" &&
      initialConfigRef.current !== null &&
      JSON.stringify(discountValidationResult) !== initialConfigRef.current
    : true;

  const handleCreateOrUpdateDiscount = useHandler(async () => {
    if (typeof discountValidationResult === "string") {
      return;
    }

    if (onConfigurationReady) {
      onConfigurationReady(discountValidationResult);
      onModalCloseRequested();
      return;
    }

    try {
      if (existingDiscount) {
        await updateDiscount({
          discountId: existingDiscount._id,
          discountConfiguration: discountValidationResult,
        });
      } else {
        const discountId = await createDiscount({
          discountConfiguration: discountValidationResult,
        });
        valueChange?.(discountId);
      }
      onModalCloseRequested();
    } catch (e: unknown) {
      if (e instanceof Error) {
        toast(e.message, { variant: "error" });
      }
    }
  });

  const filteredDiscountSettingsTypeOptions: RedoListItem<DiscountSettingsType>[] =
    useMemo(() => {
      const labels: Record<DiscountSettingsType, string> = {
        [DiscountSettingsType.BASIC]: "Percentage or amount off",
        [DiscountSettingsType.FREE_SHIPPING]: "Free shipping",
        [DiscountSettingsType.BXGY]: "Buy X Get Y",
        [DiscountSettingsType.DYNAMIC_RANGE]: "Dynamic range",
      };
      const types = allowedDiscountTypes ?? [
        DiscountSettingsType.BASIC,
        DiscountSettingsType.FREE_SHIPPING,
        DiscountSettingsType.BXGY,
      ];
      return types
        .filter((type) => {
          if (
            discountProvider === DiscountProvider.Bigcommercediscount &&
            type === DiscountSettingsType.BXGY
          ) {
            return false;
          }
          return true;
        })
        .map((type) => ({
          id: type,
          type: "text" as const,
          value: type,
          text: labels[type],
        }));
    }, [allowedDiscountTypes, discountProvider]);

  // Auto-set type when only one option is allowed
  useEffect(() => {
    const firstOption = filteredDiscountSettingsTypeOptions[0];
    if (filteredDiscountSettingsTypeOptions.length === 1 && firstOption) {
      formState.setDiscountSettingsType(firstOption.value);
    }
  }, [filteredDiscountSettingsTypeOptions, formState]);

  const isDynamicRange =
    formState.discountSettingsType === DiscountSettingsType.DYNAMIC_RANGE;

  const sectionProps: DiscountSettingsSectionProps = {
    formState,
    category,
    showHiddenRecoverFields,
    setShowHiddenRecoverFields,
  };

  const SettingsComponent = useMemo(
    () =>
      settingsComponentMap[discountProvider]?.[
        formState.discountSettingsType
      ] ?? null,
    [formState.discountSettingsType, discountProvider],
  );

  return (
    <RedoModal
      footerBorder
      headerBorder
      headerIconAlignment="inline"
      isOpen={isOpen}
      modalSize="md"
      onModalCloseRequested={onModalCloseRequested}
      primaryButton={{
        text: onConfigurationReady
          ? "Save configuration"
          : existingDiscount
            ? "Update discount"
            : "Create discount",
        disabled:
          typeof discountValidationResult === "string"
            ? discountValidationResult
            : !isDirty,
        onClick: handleCreateOrUpdateDiscount,
        size: "sm",
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: () => {
          onModalCloseRequested();
        },
        size: "sm",
      }}
      subtitle={
        existingDiscount
          ? "Updating your discount may be disruptive for audience members who have already received it"
          : undefined
      }
      theme={existingDiscount ? "warn" : undefined}
      title={
        onConfigurationReady
          ? "Configure discount"
          : existingDiscount
            ? "Update discount"
            : "Create discount"
      }
      TitleIcon={DiscountIcon}
    >
      <Flex dir="column" gap="xl">
        <RedoTextInput
          label="Name"
          placeholder="Add a name"
          setValue={formState.setDiscountName}
          value={formState.discountName}
        />
        {/* Discount Type Selector */}
        {!existingDiscount &&
          filteredDiscountSettingsTypeOptions.length > 1 && (
            <RedoSingleSelectDropdownInput<DiscountSettingsType>
              label="Discount type"
              options={filteredDiscountSettingsTypeOptions}
              optionSelected={({ item }) => {
                formState.setDiscountSettingsType(item.value);
              }}
              selectedItem={filteredDiscountSettingsTypeOptions.find(
                (o) => o.value === formState.discountSettingsType,
              )}
              size="xs"
            />
          )}
        {!isDynamicRange && (
          <RedoSingleSelectDropdownInput<DiscountGroupType>
            description={getDiscountCodeStrategyDescription({
              prefix: formState.codeStrategyName,
              type: formState.codeGenerationStrategy,
            })}
            disabled={!!existingDiscount}
            disabledItems={
              isCampaignTemplate ? [DiscountGroupType.DYNAMIC] : undefined
            }
            disabledTooltips={
              isCampaignTemplate
                ? {
                    [DiscountGroupType.DYNAMIC]:
                      "Dynamic discounts cannot be used within campaigns",
                  }
                : undefined
            }
            label="Code generation strategy"
            options={discountGroupTypeOptions}
            optionSelected={({ item }) => {
              formState.setCodeGenerationStrategy(item.value);
            }}
            placeholder="Select a discount type"
            selectedItem={discountGroupTypeOptions.find(
              (o) => o.id === formState.codeGenerationStrategy,
            )}
            size="xs"
          />
        )}
        <RedoTextInput
          fullWidth
          label={discountCodeNameTitle[formState.codeGenerationStrategy]}
          placeholder="Name your code"
          setValue={formState.setCodeStrategyName}
          state={existingDiscount ? "disabled" : "default"}
          value={formState.codeStrategyName}
        />

        {SettingsComponent && <SettingsComponent {...sectionProps} />}
      </Flex>
    </RedoModal>
  );
});

const settingsComponentMap: Record<
  DiscountProvider,
  Record<
    DiscountSettingsType,
    React.FC<DiscountSettingsSectionProps> | null
  > | null
> = {
  [DiscountProvider.Bigcommercediscount]: {
    [DiscountSettingsType.BASIC]: BigcommerceBasicSettings,
    [DiscountSettingsType.FREE_SHIPPING]: BigcommerceFreeShippingSettings,
    [DiscountSettingsType.BXGY]: null,
    [DiscountSettingsType.DYNAMIC_RANGE]: null,
  },
  [DiscountProvider.Shopifydiscount]: {
    [DiscountSettingsType.BASIC]: ShopifyBasicSettings,
    [DiscountSettingsType.FREE_SHIPPING]: ShopifyFreeShippingSettings,
    [DiscountSettingsType.BXGY]: ShopifyBxgySettings,
    [DiscountSettingsType.DYNAMIC_RANGE]: DynamicRangeSettings,
  },
};
